import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEye,
  FiEyeOff,
  FiShoppingCart,
  FiUsers,
  FiPackage,
  FiBarChart,
  FiLock,
  FiMail,
  FiArrowRight,
  FiCheckCircle,
} from 'react-icons/fi';
import AuthService from '../services/auth.service';
import SmartLoadingSpinner from './SmartLoadingSpinner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setAnimationComplete(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (user) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Load saved email on component mount (SECURITY: Never store passwords in localStorage)
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedRememberMe = localStorage.getItem('rememberMe');

    // Clear any previously stored passwords (security fix)
    localStorage.removeItem('rememberedPassword');

    if (savedRememberMe === 'true' && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await AuthService.login(email, password);

      // Save email if "Remember Me" is checked (SECURITY: Never store passwords)
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberMe', 'true');
      } else {
        // Clear saved email if "Remember Me" is unchecked
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberMe');
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FDF2F2 0%, #F8E8E8 30%, #F2D5D4 60%, #EBA3A0 100%)' }}>

      {/* Main Container */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">

          {/* Left Side - Brand & Features */}
          <div className={`transition-all duration-1000 ${animationComplete ? 'opacity-100 transform translate-x-0' : 'opacity-0 transform -translate-x-12'}`}>
            <div className="text-center lg:text-left space-y-6">
              {/* Brand Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-center lg:justify-start gap-3">
                  <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-white shadow-lg flex items-center justify-center border-2 border-[#EBA3A0]/30">
                    <img
                      src="/POS.jpg"
                      alt="Shree Gurukrupa Dairy"
                      className="w-11 h-11 lg:w-13 lg:h-13 object-contain rounded-full"
                    />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-[#B83A36]">
                      POS & Inventory System
                    </h1>
                    <p className="text-[#343232]/60 text-xs lg:text-sm">Milk Products & POS System</p>
                  </div>
                </div>

                <div className="space-y-1">
                  <h2 className="text-xl lg:text-2xl font-semibold text-[#343232]">
                    Welcome to Your Dairy Management
                  </h2>
                  <p className="text-[#343232]/60 text-sm lg:text-base">
                    Streamline your milk business with our comprehensive POS and inventory management system
                  </p>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-[#EBA3A0]/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D64641]/10 rounded-lg flex items-center justify-center">
                      <FiShoppingCart className="text-[#D64641] text-lg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#343232] text-sm">POS System</h3>
                      <p className="text-xs text-[#343232]/60">Quick sales & billing</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-[#EBA3A0]/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#EBA3A0]/20 rounded-lg flex items-center justify-center">
                      <FiPackage className="text-[#B83A36] text-lg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#343232] text-sm">Inventory</h3>
                      <p className="text-xs text-[#343232]/60">Stock management</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-[#EBA3A0]/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D64641]/10 rounded-lg flex items-center justify-center">
                      <FiUsers className="text-[#D64641] text-lg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#343232] text-sm">Customers</h3>
                      <p className="text-xs text-[#343232]/60">Customer management</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-[#EBA3A0]/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#EBA3A0]/20 rounded-lg flex items-center justify-center">
                      <FiBarChart className="text-[#B83A36] text-lg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#343232] text-sm">Reports</h3>
                      <p className="text-xs text-[#343232]/60">Analytics & insights</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form  remvoe icone and add new tagline*/}
          <div className={`transition-all duration-1000 delay-300 ${animationComplete ? 'opacity-100 transform translate-x-0' : 'opacity-0 transform translate-x-12'}`}>
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 lg:p-8 shadow-2xl border border-[#EBA3A0]/20 max-w-sm mx-auto">
              {/* Form Header */}
              <div className="text-center mb-6">
                {/* <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg" style={{ background: 'linear-gradient(135deg, #D64641 0%, #B83A36 100%)' }}>
                  <FiLock className="text-white text-xl" />
                </div> */}
                <h2 className="text-xl font-bold text-[#343232] mb-1">Sign In</h2>
                <p className="text-[#343232]/60 text-sm">Manage your dairy business smarter, faster, and more profitably.</p>
              </div>

              {/* Error Message */}
                {error && (
                  <div className={`transition-all duration-400 ${error ? 'opacity-100 transform scale-100' : 'opacity-0 transform scale-95'}`}>
                  <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex items-center gap-3">
                    <FiCheckCircle className="text-red-500 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                    </div>
                  </div>
                )}

              {/* Login Form */}
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#343232] mb-2 flex items-center gap-2">
                    <FiMail className="text-[#EBA3A0]" />
                      Email Address
                    </label>
                    <input
                      type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                      required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#D64641] focus:border-[#D64641] transition-all duration-300 hover:bg-gray-50/80 focus:bg-white shadow-sm"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-[#343232] mb-2 flex items-center gap-2">
                    <FiLock className="text-[#EBA3A0]" />
                      Password
                    </label>
                    <div className="relative">
                      <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                        name="password"
                    autoComplete="current-password"
                        required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-3 pr-10 rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#D64641] focus:border-[#D64641] transition-all duration-300 hover:bg-gray-50/80 focus:bg-white shadow-sm"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                            onClick={handleTogglePasswordVisibility}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#D64641] transition-colors duration-200 p-1 rounded-lg hover:bg-[#EBA3A0]/10"
                          >
                      {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me Checkbox */}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 text-[#D64641] bg-gray-100 border-gray-300 rounded focus:ring-[#D64641] focus:ring-2 transition-all duration-200 accent-[#D64641]"
                      />
                      <span className="text-sm text-[#343232]/70 group-hover:text-[#343232] transition-colors duration-200">
                        Remember me
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-4 rounded-lg text-base font-semibold text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 group"
                    style={{ background: 'linear-gradient(135deg, #D64641 0%, #B83A36 100%)' }}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = 'linear-gradient(135deg, #B83A36 0%, #8B2E2A 100%)'; }}
                    onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = 'linear-gradient(135deg, #D64641 0%, #B83A36 100%)'; }}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Signing In...
                    </>
                  ) : (
                    <>
                      Sign In
                      <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                  </button>
                </form>

              {/* Additional Info */}
              <div className="mt-6 pt-4 border-t border-[#EBA3A0]/20">
                <div className="text-center text-xs text-[#343232]/50">
                  <p>Secure login powered by modern authentication</p>
                  {/* <p className="mt-1">&copy; 2026 THINKTANKER. All rights reserved.</p> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Spinner */}
      <SmartLoadingSpinner
        loading={loading}
        size={80}
        color="#D64641"
        overlay={true}
        blur={true}
      />
    </div>
  );
};

export default Login;
